import { useCallback } from 'react';
import uuid from 'uuid/v4';

import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { usePubSubEvent } from '@confluence/pubsub-client/entry-points/usePubSubEvent';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { usePageContentId } from '@confluence/page-context';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { FORCE_TRANSITION_QUERY_PARAM } from '@confluence/named-routes/entry-points/force-transition';
import { useShowConvertToLivePageFlag } from '@confluence/editor-flags/entry-points/useShowConvertToLivePageFlag';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { fg } from '@confluence/feature-gating';

type ConvertToLivePageFlagProps = {
	flags: FlagsStateContainer;
};

export const ConvertToLivePageFlag = withFlags(({ flags }: ConvertToLivePageFlagProps) => {
	const [contentId] = usePageContentId();
	const { userId } = useSessionData();
	const { setQueryParams } = useRouteActions();
	const showConvertToLivePageFlag = useShowConvertToLivePageFlag({ flags });

	const onEvent = useCallback(
		(_: any, payload: any) => {
			const { accountId } = payload;
			showConvertToLivePageFlag({ converterWasCurrentUser: accountId === userId });

			// When another user converts the page to a live doc, we need to add a query param to avoid the double action bar bug.
			// Without this, the page may read stale SSR values indicating it's not a live page, causing UI weirdness.
			// The query param forces RouteState to increment transitionId, preventing it from using initial SSR values.
			setQueryParams({ [FORCE_TRANSITION_QUERY_PARAM]: uuid() });
		},
		[showConvertToLivePageFlag, userId, setQueryParams],
	);

	const onSubscriptionEvent = useCallback(
		(response: any) => {
			const { accountId, pageUpdated } = response || {};
			const trigger = pageUpdated?.trigger;

			if (trigger !== 'convertToLiveEdit') {
				return;
			}
			showConvertToLivePageFlag({ converterWasCurrentUser: accountId === userId });

			// When another user converts the page to a live doc, we need to add a query param to avoid the double action bar bug.
			// Without this, the page may read stale SSR values indicating it's not a live page, causing UI weirdness.
			// The query param forces RouteState to increment transitionId, preventing it from using initial SSR values.
			setQueryParams({ [FORCE_TRANSITION_QUERY_PARAM]: uuid() });
		},
		[showConvertToLivePageFlag, userId, setQueryParams],
	);

	usePubSubEvent({
		// contentId might be undefined coming from usePageContentId, skipping pubsub event if so
		skip:
			!contentId ||
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			(fg('cc_fps_migration_page-updated') &&
				fg('confluence_enable_subscriptions-context-provider') &&
				StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', '') ===
					'GRAPHQL'),
		// Empty contentId will not happen as pubsub subscription will be skipped
		contentId: contentId ?? '',
		contentType: 'page',
		eventName: 'convertToLiveEdit:page',
		onEvent: (_, payload) => {
			if (
				!['DUAL', 'GRAPHQL'].includes(
					StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
				) ||
				!fg('cc_fps_migration_page-updated')
			) {
				// Passing in an inline function that calls `onEvent` is necessary here due to a bug,
				// where callbacks passed directly into `usePubSubEvent` don't get called sometimes.
				// This change was made to fix MODES-6451.
				onEvent(_, payload);
			}
		},
	});

	useSubscriptionEvent({
		subscriberId: 'convertToLivePageFlag',
		contentId,
		delta: 'pageUpdated',
		contentType: 'page',
		skip:
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			!contentId ||
			!fg('cc_fps_migration_page-updated') ||
			!fg('confluence_enable_subscriptions-context-provider') ||
			!['DUAL', 'GRAPHQL'].includes(
				StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
			),
		// Passing in an inline function that calls `onEvent` is necessary here due to a bug,
		// where callbacks passed directly into `usePubSubEvent` don't get called sometimes.
		// copied the change that was made to fix MODES-6451.
		onDataReceived: (response) => onSubscriptionEvent(response),
	});

	return null;
});
